import { createFileRoute } from '@tanstack/react-router'
import { noop, useSuspenseQuery } from '@tanstack/react-query'
import { trpc } from '@/main'
import { useLingui } from '@lingui/react/macro'
import { useState } from 'react'
import { Background } from '@/routes/_landing/-components/background'
import { SlideShow } from '@/routes/_landing/-components/slideshow'
import { Pricing } from '@/routes/_landing/-components/pricing'
import { ContactDialog } from '@/routes/_landing/-components/contact-dialog'
import { ProfilesCount, ProfilesCountValue } from '@/routes/_landing/-components/profiles-count'
import { ThemedVideo } from '@/routes/_landing/-components/themed-video'
import { assetUrl } from '@/lib/asset-url'
import {
  IconDescribeYourJob,
  IconInviteYourApplicants,
  IconCompareResults,
  IconAnalyzeYourTeams,
  IconAnalyzeMarket,
  IconIntegrateOurAPI,
} from '@/components/custom/icons'
import { Feature } from './-components/feature'
import { ButtonPrimitive } from '@/components/ui/button-primitive'

export const Route = createFileRoute('/_landing/')({
  context: () => ({ profilesCountQueryOptions: trpc.common.getProfilesCount.queryOptions() }),
  loader: ({ context }) => {
    context.queryClient.query(context.profilesCountQueryOptions).catch(noop)
  },
  component: RouteComponent,
  staticData: { landingHeader: { aboveBackground: true } },
})

function RouteComponent() {
  const { t } = useLingui()
  const [openContact, setOpenContact] = useState(false)

  return (
    <>
      <Background
        style={{
          background: `
            radial-gradient(at 20% 6%, hsl(163, 99%, 40%) 0, transparent 100%),
            radial-gradient(at 81% 11%, hsl(242, 100%, 70%) 0, transparent 75%)
          `,
        }}
      />

      <section className="mb-15 flex flex-col items-center">
        <div className="max-w-225 p-8 text-center">
          <h1 className="mb-8 text-[42px] leading-tight font-bold whitespace-pre-wrap text-white md:text-[42px]">
            {t`The easiest and fastest way\nto evaluate talent`}
          </h1>
          <p className="mb-0 text-[18px] leading-normal font-medium whitespace-pre-wrap text-white">
            {t`Identify the professionals that best suit your positions,\nget to know your teams and discover the profiles that the market demands.`}
          </p>
          <a
            href="/register/company"
            className="mt-7.5 mb-2.5 inline-block rounded-[5px] bg-white px-3 py-3 text-[18px] font-bold text-black transition-shadow hover:shadow-[0px_0px_15px_-5px_white]"
          >
            {t`Get started for free`}
          </a>
          <div className="my-6.25">
            <ProfilesCount className="text-[18px] font-bold text-white">
              <ProfilesCountReader className="text-[18px] font-bold text-white" />
            </ProfilesCount>
            <span className="ml-2 text-[18px] font-medium text-white">{t`profiles analyzed`}</span>
          </div>
        </div>

        <div className="px-5">
          <ThemedVideo
            light={assetUrl('videos/landing.mp4')}
            dark={assetUrl('videos/landing-dark.mp4')}
            posterLight={assetUrl('videos/landing.png')}
            posterDark={assetUrl('videos/landing-dark.png')}
            className="h-165 w-225 rounded-[10px] shadow-2xl max-md:h-auto max-md:w-full"
          />
        </div>
      </section>

      <SlideShow />

      <Feature
        title={t`Describe your positions`}
        Icon={IconDescribeYourJob}
        description={t`Choose the relevant and non-relevant metrics for a position and create the ideal profile for the person who should perform it.`}
        video={assetUrl('videos/describe-job.mp4')}
        videoDark={assetUrl('videos/describe-job-dark.mp4')}
        image={assetUrl('videos/describe-job.png')}
        imageDark={assetUrl('videos/describe-job-dark.png')}
        width={900 / 1.5}
        height={560 / 1.5}
        link={{
          text: t`Describe your first position`,
          url: '/register/company',
        }}
      />

      <Feature
        title={t`Invite your professionals`}
        Icon={IconInviteYourApplicants}
        description={t`Invite your applicants and professionals to fill out our tests.\nIt's fast, reliable, and it will add value to their careers.`}
        video={assetUrl('videos/test.mp4')}
        videoDark={assetUrl('videos/test-dark.mp4')}
        image={assetUrl('videos/test.png')}
        imageDark={assetUrl('videos/test-dark.png')}
        width={482 / 1.2}
        height={542 / 1.2}
        link={{
          text: t`Invite them now`,
          url: '/register/company',
        }}
      />

      <Feature
        title={t`Compare the results`}
        Icon={IconCompareResults}
        description={t`See your applicants' and professionals' Psychographic Profile, discover how they adjust to your positions and get personalized tips to prepare your interviews.`}
        video={assetUrl('videos/compare.mp4')}
        videoDark={assetUrl('videos/compare-dark.mp4')}
        image={assetUrl('videos/compare.png')}
        imageDark={assetUrl('videos/compare-dark.png')}
        width={900 / 1.5}
        height={700 / 1.5}
        link={{
          text: t`Start comparing`,
          url: '/register/company',
        }}
      />

      <Feature
        title={t`Analyze your teams`}
        Icon={IconAnalyzeYourTeams}
        description={t`Compare the profile for each of your teams with that of their individual members. Identify potential matchings and optimize the team's performance.`}
        video={assetUrl('videos/teams.mp4')}
        videoDark={assetUrl('videos/teams-dark.mp4')}
        image={assetUrl('videos/teams.png')}
        imageDark={assetUrl('videos/teams-dark.png')}
        width={800 / 1.5}
        height={800 / 1.5}
        link={{
          text: t`Create your first team`,
          url: '/register/company',
        }}
      />

      <Feature
        title={t`Explore the market`}
        Icon={IconAnalyzeMarket}
        description={t`Discover the standard profile of professionals performing the positions you are interested in. Analyze the global market interactively.`}
        video={assetUrl('videos/market.mp4')}
        videoDark={assetUrl('videos/market-dark.mp4')}
        image={assetUrl('videos/market.png')}
        imageDark={assetUrl('videos/market-dark.png')}
        width={900 / 1.5}
        height={800 / 1.5}
        link={{
          text: t`Access the market`,
          url: '/register/company',
        }}
      />

      <Feature
        title={t`Integrate our API`}
        Icon={IconIntegrateOurAPI}
        description={t`Embed our solutions in your platform fast and easily.\nUse our SDK to enable your users and our API to query the generated data.`}
        video={assetUrl('videos/api.mp4')}
        videoDark={assetUrl('videos/api.mp4')}
        image={assetUrl('videos/api.png')}
        imageDark={assetUrl('videos/api.png')}
        width={700 / 1.2}
        height={400 / 1.2}
        link={{
          text: t`Go to documentation`,
          url: '/docs',
        }}
      />

      <Pricing setOpenContact={setOpenContact} userType="company" />

      <h2 className="mb-0 text-center text-[20px] font-semibold text-gray-800 dark:text-gray-200">
        {t`Any questions?`}
      </h2>
      <ButtonPrimitive
        type="button"
        onClick={() => setOpenContact(true)}
        className="text-primary mx-auto mt-0 mb-50 flex cursor-pointer items-center border-0 bg-transparent text-[20px] font-medium"
      >
        {t`Contact us`}
      </ButtonPrimitive>

      <ContactDialog openContact={openContact} setOpenContact={setOpenContact} />
    </>
  )
}

/**
 * Reads the counter INSIDE the boundary `<ProfilesCount>` puts up, so a slow or failing count never
 * blocks this page. It lives in the route file because that is where this route's context is.
 */
function ProfilesCountReader(props: { className?: string }) {
  const { data } = useSuspenseQuery(Route.useRouteContext().profilesCountQueryOptions)
  return <ProfilesCountValue className={props.className} profilesCount={data.profilesCount} />
}
